import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Download, ShoppingBag, ArrowRight } from 'lucide-react';
import { CreateOrderResponse } from '@/services/ordersApi';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<CreateOrderResponse | null>(null);

  useEffect(() => {
    // Get order data from navigation state
    const stateOrderData = location.state?.orderData;
    if (stateOrderData) {
      setOrderData(stateOrderData);
    } else if (orderId) {
      // If no state data, redirect to account page
      navigate('/account?tab=orders');
    }
  }, [location.state, orderId, navigate]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(price);
  };

  const getStateVariant = (color: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (color) {
      case 'yellow':
        return 'outline';
      case 'green':
        return 'secondary';
      case 'red':
        return 'destructive';
      default:
        return 'default';
    }
  };

  if (!orderData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <Card>
            <CardContent className="pt-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Chargement des détails de commande...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <Card className="mb-8 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <h1 className="text-2xl font-bold text-green-800">
                Commande confirmée !
              </h1>
              <p className="text-green-700">
                Votre commande a été créée avec succès. Vous recevrez un email de confirmation sous peu.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Détails de la commande
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Numéro de commande
                </Label>
                <p className="text-lg font-bold text-primary">{orderData.name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Montant total
                </Label>
                <p className="text-lg font-bold">{formatPrice(orderData.amount)}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Statut
                </Label>
                <div className="mt-1">
                  <Badge variant={getStateVariant(orderData.state_color)}>
                    {orderData.state_label}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Mode de paiement
                </Label>
                <p className="mt-1 capitalize">{orderData.payment_method}</p>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                Mode de livraison
              </Label>
              <p className="mt-1 capitalize">{orderData.delivery_method}</p>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Prochaines étapes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </div>
              <div>
                <p className="font-medium">Confirmation par email</p>
                <p className="text-sm text-muted-foreground">
                  Vous recevrez un email de confirmation avec les détails de votre commande.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </div>
              <div>
                <p className="font-medium">Traitement de la commande</p>
                <p className="text-sm text-muted-foreground">
                  Notre équipe va préparer votre commande dans les plus brefs délais.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                3
              </div>
              <div>
                <p className="font-medium">Suivi de commande</p>
                <p className="text-sm text-muted-foreground">
                  Vous pouvez suivre l'état de votre commande dans votre espace client.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            onClick={() => navigate('/account?tab=orders')} 
            className="flex-1"
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Voir mes commandes
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => navigate('/')} 
            className="flex-1"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Continuer mes achats
          </Button>
        </div>

        {/* Support Info */}
        <Card className="mt-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-blue-800">Besoin d'aide ?</h3>
              <p className="text-sm text-blue-700">
                Notre équipe support est à votre disposition pour toute question concernant votre commande.
              </p>
              <Button variant="outline" size="sm" className="mt-3">
                Contacter le support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Label = ({ className, children, ...props }: { className?: string; children: React.ReactNode }) => (
  <label className={className} {...props}>{children}</label>
);

export default OrderConfirmation;